import type { HttpClient } from '../http/HttpClient.ts';

export type Source = 'organization' | 'project';

export interface ContextDoc {
  id: string;
  name: string;
  body: string;
  source: Source;
  revision: number;
  updatedAt: string;
  updatedBy: { id: string; name: string } | null;
  /** A project document that replaces the organization's document of the same name. */
  replaces: boolean;
}

export interface WhoPerson {
  id: string;
  user: { id: string; name: string };
  responsibility: string;
  areas: string[];
  source: Source;
}

export interface RuleSet {
  allowAgents: boolean;
  allowWrites: boolean;
  allowMessaging: boolean;
  allowedScopes: string[] | null;
}

export interface Rules {
  effective: RuleSet;
  organization: RuleSet;
  project: {
    allowAgents: boolean | null;
    allowWrites: boolean | null;
    allowMessaging: boolean | null;
  } | null;
}

export interface AgentContextView {
  docs: ContextDoc[];
  people: WhoPerson[];
  rules: Rules;
}

export interface ContextVersion {
  id: string;
  body: string;
  createdAt: string;
  by: { id: string; name: string } | null;
}

/** Where context lives: an organization, or one of its projects. */
export interface ContextPlace {
  org: string;
  /** Null: the organization's own context. */
  key: string | null;
}

const base = ({ org, key }: ContextPlace) =>
  key
    ? `/orgs/${encodeURIComponent(org)}/projects/${encodeURIComponent(key)}/agent-context`
    : `/orgs/${encodeURIComponent(org)}/agent-context`;

/** Agent context: documents, Who's who and rules, for an organization or a project. */
export class AgentContextApi {
  readonly #http: HttpClient;

  constructor(http: HttpClient) {
    this.#http = http;
  }

  get(place: ContextPlace): Promise<AgentContextView> {
    return this.#http.get(base(place));
  }

  createDoc(
    place: ContextPlace,
    input: { name: string; body: string },
  ): Promise<{ doc: ContextDoc }> {
    return this.#http.post(`${base(place)}/docs`, input);
  }

  updateDoc(
    place: ContextPlace,
    id: string,
    change: { name?: string; body?: string; expectedRevision?: number },
  ): Promise<void> {
    return this.#http.patch(`${base(place)}/docs/${encodeURIComponent(id)}`, change);
  }

  deleteDoc(place: ContextPlace, id: string): Promise<void> {
    return this.#http.delete(`${base(place)}/docs/${encodeURIComponent(id)}`);
  }

  versions(place: ContextPlace, id: string): Promise<{ versions: ContextVersion[] }> {
    return this.#http.get(`${base(place)}/docs/${encodeURIComponent(id)}/versions`);
  }

  restore(place: ContextPlace, id: string, versionId: string): Promise<void> {
    return this.#http.post(
      `${base(place)}/docs/${encodeURIComponent(id)}/versions/${encodeURIComponent(versionId)}/restore`,
    );
  }

  addPerson(
    place: ContextPlace,
    input: { userId: string; responsibility: string; areas: string[] },
  ): Promise<AgentContextView> {
    return this.#http.post(`${base(place)}/people`, input);
  }

  updatePerson(
    place: ContextPlace,
    id: string,
    change: { responsibility?: string; areas?: string[] },
  ): Promise<void> {
    return this.#http.patch(`${base(place)}/people/${encodeURIComponent(id)}`, change);
  }

  removePerson(place: ContextPlace, id: string): Promise<void> {
    return this.#http.delete(`${base(place)}/people/${encodeURIComponent(id)}`);
  }

  setRules(
    place: ContextPlace,
    change: Partial<{
      allowAgents: boolean | null;
      allowWrites: boolean | null;
      allowMessaging: boolean | null;
    }>,
  ): Promise<{ rules: Rules }> {
    return this.#http.put(`${base(place)}/rules`, change);
  }
}
